import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Gift, CheckCircle2, Mail, Copy, Loader2, Home } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GiftVoucherSuccess() {
    const [searchParams] = useSearchParams();
    const voucherId = searchParams.get('voucher_id');
    const [loading, setLoading] = useState(true);
    const [voucher, setVoucher] = useState(null);

    useEffect(() => {
        if (voucherId) {
            confirmAndFetchVoucher();
        }
    }, [voucherId]);

    const confirmAndFetchVoucher = async () => {
        try {
            // Confirm payment first
            await axios.post(`${API}/gift-vouchers/confirm/${voucherId}`);
            
            // Fetch voucher details
            const response = await axios.get(`${API}/gift-vouchers/${voucherId}`);
            setVoucher(response.data);
        } catch (error) {
            toast.error('Failed to load voucher details');
        } finally {
            setLoading(false);
        }
    };

    const copyCode = () => {
        if (voucher?.code) {
            navigator.clipboard.writeText(voucher.code);
            toast.success('Voucher code copied!');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                    <p className="text-muted-foreground">Confirming your purchase...</p>
                </div>
            </div>
        );
    }

    if (!voucher) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <Card className="max-w-md w-full text-center p-8">
                    <h2 className="text-xl font-semibold mb-4">Voucher Not Found</h2>
                    <p className="text-muted-foreground mb-6">
                        We couldn't find this voucher. Please contact support if you believe this is an error.
                    </p>
                    <Link to="/gift-vouchers">
                        <Button>Back to Gift Vouchers</Button>
                    </Link>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-green-50 to-background py-16 px-4">
            <div className="container mx-auto max-w-2xl">
                {/* Success Header */}
                <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="w-10 h-10 text-green-600" />
                    </div>
                    <h1 className="text-3xl font-bold text-foreground mb-2">
                        Thank You for Your Purchase!
                    </h1>
                    <p className="text-muted-foreground">
                        Your gift voucher has been created successfully
                    </p>
                </div>

                {/* Voucher Card */}
                <Card className="mb-8 overflow-hidden">
                    <div className="bg-primary p-6 text-white text-center">
                        <Gift className="w-12 h-12 mx-auto mb-3" />
                        <h2 className="text-2xl font-bold">Craven Canines Gift Voucher</h2>
                    </div>
                    <CardContent className="p-6 space-y-6">
                        {/* Voucher Code */}
                        <div className="bg-secondary/50 rounded-xl p-4 text-center">
                            <p className="text-sm text-muted-foreground mb-2">Voucher Code</p>
                            <div className="flex items-center justify-center gap-3">
                                <code className="text-2xl font-bold font-mono tracking-wider">
                                    {voucher.code}
                                </code>
                                <Button variant="outline" size="sm" onClick={copyCode}>
                                    <Copy className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>

                        {/* Voucher Details */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-muted-foreground">Value</p>
                                <p className="text-2xl font-bold text-primary">£{voucher.amount}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Type</p>
                                <Badge className="capitalize">{voucher.voucher_type}</Badge>
                                {voucher.session_name && (
                                    <p className="text-sm mt-1">{voucher.session_name}</p>
                                )}
                            </div>
                        </div>

                        {/* Recipient Info */}
                        <div className="border-t pt-4">
                            <p className="text-sm text-muted-foreground mb-2">Gift For</p>
                            <p className="font-semibold">{voucher.recipient_name}</p>
                            <p className="text-muted-foreground">{voucher.recipient_email}</p>
                        </div>

                        {/* Personal Message */}
                        {voucher.personal_message && (
                            <div className="bg-secondary/30 rounded-lg p-4 border-l-4 border-primary">
                                <p className="text-sm text-muted-foreground mb-1">Your Message</p>
                                <p className="italic">"{voucher.personal_message}"</p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Info */}
                <Card className="mb-8">
                    <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                            <Mail className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                            <div>
                                <h3 className="font-semibold mb-1">Email Confirmation</h3>
                                <p className="text-sm text-muted-foreground">
                                    We've sent a confirmation email to <strong>{voucher.purchaser_email}</strong> with the voucher details.
                                    The recipient ({voucher.recipient_email}) will also receive their voucher code.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* How to Redeem */}
                <Card className="mb-8">
                    <CardContent className="p-6">
                        <h3 className="font-semibold mb-4">How to Redeem</h3>
                        <ol className="space-y-3 text-sm text-muted-foreground">
                            <li className="flex gap-3">
                                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 font-semibold">1</span>
                                <span>Visit our Mantrailing page and book a session</span>
                            </li>
                            <li className="flex gap-3">
                                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 font-semibold">2</span>
                                <span>Proceed to checkout and complete the safety form</span>
                            </li>
                            <li className="flex gap-3">
                                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 font-semibold">3</span>
                                <span>Enter the voucher code in the discount code field</span>
                            </li>
                            <li className="flex gap-3">
                                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 font-semibold">4</span>
                                <span>The voucher value will be applied to your order</span>
                            </li>
                        </ol>
                    </CardContent>
                </Card>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/">
                        <Button variant="outline" className="w-full sm:w-auto">
                            <Home className="w-4 h-4 mr-2" />
                            Back to Home
                        </Button>
                    </Link>
                    <Link to="/gift-vouchers">
                        <Button className="w-full sm:w-auto btn-primary">
                            <Gift className="w-4 h-4 mr-2" />
                            Buy Another Voucher
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
    );
}
